import { AskDataMode } from './AskDataMode';
import { ISiteCommentingSettings } from './ISiteCommentingSettings';

// a generic interface that allows us to pass bridge settings, extension settings, etc to the coordinator ctrl
type SiteAdminSettingsCombined = ISiteAdminSettings | IExtensionsSiteSettings | ISiteAdminBridgeSettings;

export interface ISiteSettingsForSiteAdmin {
  siteId: string;
  siteAdminSettings: SiteAdminSettingsCombined;
  subscriptionSettings?: ISiteSubscriptionSettings;
}

export interface ISiteAdminSettings {
  addSupportUserEnabled: boolean;
  alternativeFederationEngineEnabled: boolean;
  derivedPermissionsEnabled: boolean;
  notificationEnabled: boolean;
  refreshTokenEnabled: boolean;
  remoteAgentSettings?: IRemoteAgentSiteSettings;
  remoteQueryingEnabled: boolean;
  sheetImageEnabled: boolean;
  siteInviteEmailEnabled: boolean;
  extractEncryptionMode: string;
  isAdminDataSourceEnabled: boolean;
  siteStartPageUri: string;
  siteNamedSharingSettings: ISiteNamedSharingSettings;
  vizRecommendationsEnabled: boolean;
  vizRecUsernameVisibilityEnabled: boolean;
  siteSandboxSettings: ISiteSandboxSettings;
  askDataMode: AskDataMode;
  userVisibility: UserVisibilityMode;
  requestAccessEnabled: boolean;
  siteCommentingSettings?: ISiteCommentingSettings;
  dataAlertsEnabled: boolean;
  metricsContentTypeEnabled: boolean;
  notifySiteAdminsOnThrottleEnabled: boolean;
  runNowEnabled: boolean;
  catalogObfuscationEnabled: boolean;
}

export interface ISiteNamedSharingSettings {
  namedSharingEnabled: boolean;
}

export interface ISiteSandboxSettings {
  sandboxEnabled: boolean;
  sandboxDatasourcesEnabled: boolean;
  sandboxFlowsEnabled: boolean;
  sandboxStorageQuotaSet: boolean;
  sandboxStorageQuota: number;
}

export interface ISiteBridgeSettingsForSiteAdmin {
  siteId: string;
  siteAdminSettings: ISiteAdminBridgeSettings;
}

export interface ISiteAdminBridgeSettings {
  remoteQueryingEnabled: boolean;
  remoteAgentSettings?: IRemoteAgentSiteSettings;
}

export interface IRemoteAgentSiteSettings {
  agents?: IRemoteQueryAgent[];
  notificationForClientNotRunningEnabled?: boolean;
}

export interface IRemoteQueryAgent {
  id: string;
  agentName: string;
  ownerId: string;
  ownerFriendlyName: string;
  deviceId: string;
  connectionStatus: string;
  lastUsed: string; // ISO 8601 format: YYYY-MM-DDThh:mm:ssZ
  pooled: boolean;
  modifiable: boolean;
  inServiceMode: boolean;
  extractRefreshDatasourceCount: number;
  supportsLiveQuery: boolean;
  version?: string;
}

export interface ExtensionSafeListEntry {
  url: string;
  fullDataAllowed: boolean;
  promptNeeded: boolean;
}

export interface Option {
  name: string;
  value: boolean;
}

export interface ISiteExtensionsSettingsForSiteAdmin {
  siteId: string;
  featureSettings: IExtensionsSiteSettings;
}

export interface IExtensionsSiteSettings {
  extensionsEnabled: boolean;
  allowSandboxed: boolean;
  safeList: ExtensionSafeListEntry[];
}

export enum UserVisibilityMode {
  FULL = 'FULL',
  LIMITED = 'LIMITED',
}

export enum UserQuotaMode {
  SERVER_LIMIT = 'server-limit',
  SITE_USER_QUOTA = 'site-user-quota',
  SITE_TIERED_CAPACITY = 'site-tiered-capacity'
}

export interface ISiteSubscriptionSettings {
  subscriptionsEnabled: boolean;
  subscribeOthersEnabled: boolean;
  customScheduleSubscriptionForSiteEnabled: boolean;
  allowSubscriptionAttachments: boolean;
  serverAllowSubscriptionAttachments: boolean;
}
